import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronLeft, FaChevronRight, FaRegCalendarAlt } from "react-icons/fa";
import { twMerge } from "tailwind-merge";

/* ─── value helpers ──────────────────────────────────────────────────────── */

function splitValue(value?: string | null): { date: string; time: string } {
  if (!value) return { date: "", time: "" };
  const [date = "", timePart = ""] = value.split("T");
  const time = timePart.slice(0, 5);
  return { date, time };
}

function joinValue(date: string, time: string): string {
  if (!date) return "";
  if (!time) return date;
  return `${date}T${time}`;
}

/** Join separate date (`YYYY-MM-DD`) + time (`HH:mm`) into a DateTimePicker value. */
export function joinDateTime(date?: string | null, time?: string | null): string {
  return joinValue(date || "", time || "");
}

/** Split a DateTimePicker value back into date + time fields. */
export function splitDateTime(value?: string | null): { date: string; time: string } {
  return splitValue(value);
}

/* ─── date helpers ───────────────────────────────────────────────────────── */

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

function parseDateValue(value?: string | null): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

function formatDateValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateLabel(value?: string | null): string {
  const date = parseDateValue(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = MONTHS[date.getMonth()].slice(0, 3);
  return `${day} ${month} ${date.getFullYear()}`;
}

function formatTimeLabel(value?: string | null): string {
  if (!value) return "";
  const [hourStr, minuteStr] = value.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return "";
  const period = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
}

function formatDateTimeLabel(value?: string | null): string {
  const { date, time } = splitValue(value);
  if (!date) return "";
  const dateLabel = formatDateLabel(date);
  if (!time) return dateLabel;
  return `${dateLabel}, ${formatTimeLabel(time)}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function buildCalendarDays(visibleMonth: Date): Date[] {
  const first = startOfMonth(visibleMonth);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());

  const days: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + i);
    days.push(day);
  }
  return days;
}

interface TimeOption {
  value: string;
  label: string;
}

function buildTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 1) {
      const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      options.push({ value, label: formatTimeLabel(value) });
    }
  }
  return options;
}

function normalizeTimeValue(value?: string | null): string {
  if (!value) return "";
  const parts = value.split(":");
  if (parts.length < 2) return "";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
}

/* ─── public DateTimePicker ──────────────────────────────────────────────── */

type DateTimePickerProps = {
  id?: string;
  label?: ReactNode;
  /** Combined local value: `YYYY-MM-DDTHH:mm`, or empty */
  value?: string | null;
  onChange?: (value: string) => void;
  /** Single trigger placeholder, e.g. "From date & time" */
  placeholder?: string;
  /** @deprecated Prefer `placeholder` — kept for callers that still pass datePlaceholder */
  datePlaceholder?: string;
  timePlaceholder?: string;
  className?: string;
  buttonClassName?: string;
  buttonStyle?: CSSProperties;
  disabled?: boolean;
  title?: string;
  /** When a date is chosen and time is empty, fill with this (`HH:mm`). */
  defaultTime?: string;
};

/**
 * Single-trigger modern date + time picker.
 * Opens one panel with calendar and time list together.
 * Value format: `YYYY-MM-DDTHH:mm`.
 */
export function DateTimePicker({
  id,
  label,
  value,
  onChange,
  placeholder,
  datePlaceholder,
  timePlaceholder,
  className = "",
  buttonClassName = "",
  buttonStyle,
  disabled = false,
  title,
  defaultTime = "00:00",
}: DateTimePickerProps) {
  const { date, time } = splitValue(value);
  const selectedDate = useMemo(() => parseDateValue(date), [date]);
  const timeOptions = useMemo(buildTimeOptions, []);
  const normalizedTime = normalizeTimeValue(time);
  const selectedTimeIndex = useMemo(
    () => timeOptions.findIndex((o) => o.value === normalizedTime),
    [timeOptions, normalizedTime],
  );

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => selectedDate ?? new Date());
  const [highlightedTimeIndex, setHighlightedTimeIndex] = useState(selectedTimeIndex);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timeOptionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const today = useMemo(() => new Date(), []);
  const days = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);
  const displayLabel = formatDateTimeLabel(value);
  const triggerPlaceholder =
    placeholder ||
    (datePlaceholder && timePlaceholder
      ? `${datePlaceholder.replace(/ date$/i, "")} date & time`
      : datePlaceholder) ||
    "Select date & time";

  const emit = useCallback(
    (nextDate: string, nextTime: string) => {
      onChange?.(joinValue(nextDate, nextTime));
    },
    [onChange],
  );

  const updateCoords = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = 420;
    const left = Math.min(rect.left, window.innerWidth - panelWidth - 8);
    setCoords({ top: rect.bottom + 4, left: Math.max(8, left) });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const handleReposition = () => updateCoords();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setVisibleMonth(selectedDate ?? new Date());
    const target = selectedTimeIndex >= 0 ? selectedTimeIndex : 0;
    setHighlightedTimeIndex(target);
    requestAnimationFrame(() => {
      timeOptionRefs.current[target]?.scrollIntoView({ block: "center" });
    });
  }, [open, selectedDate, selectedTimeIndex]);

  useEffect(() => {
    if (!open) return;
    timeOptionRefs.current[highlightedTimeIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedTimeIndex, open]);

  const commitDate = (day: Date) => {
    emit(formatDateValue(day), time || defaultTime);
  };

  const commitTime = (nextTime: string) => {
    if (!date) return;
    emit(date, nextTime);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedTimeIndex((prev) => Math.min(Math.max(prev, 0) + 1, timeOptions.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedTimeIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
      case " ": {
        event.preventDefault();
        const option = timeOptions[highlightedTimeIndex];
        if (option && date) {
          commitTime(option.value);
          setOpen(false);
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className={twMerge("flex flex-col items-start", className)} title={title}>
      {label !== undefined && (
        <label className="w-full text-gray-500 text-sm" htmlFor={id}>
          {label}
        </label>
      )}

      <div ref={containerRef} className="relative w-full">
        <button
          id={id}
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
          style={buttonStyle}
          className={twMerge(
            "flex w-full min-w-[200px] items-center justify-between rounded-md border p-2 pr-9 text-left text-dark-blue",
            "focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500",
            open ? "ring-2 ring-cyan-500/40 border-cyan-500" : "",
            disabled ? "cursor-not-allowed bg-gray-50 opacity-60" : "cursor-pointer",
            buttonClassName,
          )}
        >
          <span className={twMerge("truncate text-xs", displayLabel ? "text-dark-blue" : "text-gray-400")}>
            {displayLabel || triggerPlaceholder}
          </span>
        </button>
        <FaRegCalendarAlt className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Choose date and time"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 10000,
              width: 420,
            }}
            className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
          >
            <div className="flex">
              {/* Calendar */}
              <div className="w-[252px] border-r border-slate-100 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setVisibleMonth((m) => addMonths(m, -1))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    aria-label="Previous month"
                  >
                    <FaChevronLeft className="h-3 w-3" />
                  </button>
                  <p className="text-sm font-semibold text-slate-800">
                    {MONTHS[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}
                  </p>
                  <button
                    type="button"
                    onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                    aria-label="Next month"
                  >
                    <FaChevronRight className="h-3 w-3" />
                  </button>
                </div>

                <div className="mb-1 grid grid-cols-7 gap-0.5">
                  {WEEKDAYS.map((d) => (
                    <div
                      key={d}
                      className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5">
                  {days.map((day) => {
                    const inMonth = isSameMonth(day, visibleMonth);
                    const selectedDay = selectedDate ? isSameDay(day, selectedDate) : false;
                    const isTodayDay = isSameDay(day, today);
                    return (
                      <button
                        key={formatDateValue(day)}
                        type="button"
                        onClick={() => commitDate(day)}
                        className={twMerge(
                          "h-8 rounded-lg text-sm transition",
                          inMonth ? "text-slate-800" : "text-slate-300",
                          !selectedDay && inMonth ? "hover:bg-cyan-50" : "",
                          selectedDay ? "bg-cyan-600 font-semibold text-white hover:bg-cyan-600" : "",
                          !selectedDay && isTodayDay ? "ring-1 ring-cyan-400" : "",
                        )}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time list */}
              <div className="flex w-[168px] flex-col">
                <div className="border-b border-slate-100 px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Time
                  </p>
                </div>
                <div className="max-h-[280px] overflow-y-auto py-1">
                  {timeOptions.map((option, index) => {
                    const isSelected = index === selectedTimeIndex;
                    const isHighlighted = index === highlightedTimeIndex;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        ref={(el) => {
                          timeOptionRefs.current[index] = el;
                        }}
                        disabled={!date}
                        onMouseEnter={() => setHighlightedTimeIndex(index)}
                        onClick={() => {
                          commitTime(option.value);
                        }}
                        className={twMerge(
                          "flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-dark-blue",
                          !date ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                          isHighlighted && date ? "bg-cyan-50" : "",
                          isSelected ? "font-medium" : "",
                        )}
                      >
                        <span>{option.label}</span>
                        {isSelected && <FaCheck className="text-cyan-600" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  const todayDate = new Date();
                  setVisibleMonth(startOfMonth(todayDate));
                  emit(formatDateValue(todayDate), time || defaultTime);
                }}
                className="text-xs font-semibold text-cyan-700 hover:text-cyan-800"
              >
                Today
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    onChange?.("");
                    setOpen(false);
                  }}
                  className="text-xs font-medium text-slate-400 hover:text-slate-600"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={!date}
                  className={twMerge(
                    "rounded-md px-2.5 py-1 text-xs font-semibold",
                    date
                      ? "bg-cyan-600 text-white hover:bg-cyan-700"
                      : "cursor-not-allowed bg-slate-100 text-slate-400",
                  )}
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
